import React from "react";
import {
  Armchair, CalendarDays, Clock, Coins, Crown, Download, Gauge, Hourglass, Package, Receipt, TrendingDown, TrendingUp, Users, UtensilsCrossed,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { exportarExcel } from "@/features/relatorio-vendas/lib/excel";
import { formatCurrency, formatInt, formatPct } from "@/features/relatorio-vendas/lib/formato";
import { rotuloArquivo } from "@/features/relatorio-vendas/lib/periodo";

import { Destaques, Kpi, Nota, Secao } from "../componentes/Base";
import Faixas from "../componentes/Faixas";
import GraficoMetrica from "../componentes/GraficoMetrica";
import MapaMesas from "../componentes/MapaMesas";
import Tabela from "../componentes/Tabela";
import { comparador, formatarDecimal, formatarMinutos, reaisEixo, rendimento } from "../regras";

const porHora = (v) => (v == null ? "—" : `${formatCurrency(v)}/h`);
const minutosEixo = (v) => `${Math.round(v)} min`;

const SELO = {
  alto: { texto: "rende bem", cor: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", Icone: TrendingUp },
  baixo: { texto: "rende pouco", cor: "bg-amber-500/15 text-amber-700 dark:text-amber-400", Icone: TrendingDown },
};

function SeloRendimento({ nivel }) {
  const s = SELO[nivel];
  if (!s) return null;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${s.cor}`}>
      <s.Icone className="w-3 h-3" />{s.texto}
    </span>
  );
}

function destaques(d) {
  const k = d.kpis;
  const comVenda = d.ranking.filter((m) => m.vendas > 0);
  // Mesa com pouca ocupação não entra no "rende mais/menos por hora" (1 venda boa distorce)
  const minimo = Math.max(3, Math.round((k.vendas / Math.max(comVenda.length, 1)) * 0.3));
  const comparaveis = comVenda.filter((m) => m.vendas >= minimo && m.faturamento_hora != null);
  const porRende = [...comparaveis].sort((a, b) => b.faturamento_hora - a.faturamento_hora);
  const topo = comVenda[0];
  const pico = [...d.por_hora].sort((a, b) => b.ocupacao_media - a.ocupacao_media)[0];
  const paradas = d.ranking.filter((m) => !m.vendas && !m.excluida).length;
  const faixaTop = [...d.faixas].filter((f) => f.vendas >= 3).sort((a, b) => b.ticket_medio - a.ticket_medio)[0];
  return [
    topo && { icone: Crown, texto: <><b>{topo.nome}</b> é a que mais fatura: {formatCurrency(topo.faturamento)} ({formatPct(topo.participacao)} das mesas).</> },
    porRende.length > 1 && { icone: TrendingUp, cor: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
      texto: <><b>{porRende[0].nome}</b> é a que mais rende por hora: {porHora(porRende[0].faturamento_hora)}.</> },
    porRende.length > 1 && { icone: TrendingDown, cor: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
      texto: <><b>{porRende[porRende.length - 1].nome}</b> é a que menos rende por hora: {porHora(porRende[porRende.length - 1].faturamento_hora)}.</> },
    pico?.ocupacao_media > 0 && { icone: Gauge,
      texto: <>Pico às <b>{pico.rotulo}</b>: em média {formatarDecimal(pico.ocupacao_media)} de {k.mesas_cadastradas} mesas ocupadas.</> },
    faixaTop && { icone: Hourglass, texto: <>Quem fica <b>{faixaTop.rotulo.toLowerCase()}</b> gasta mais: ticket de {formatCurrency(faixaTop.ticket_medio)}.</> },
    paradas > 0 && { icone: Armchair, cor: "bg-muted text-muted-foreground",
      texto: <><b>{paradas} {paradas === 1 ? "mesa" : "mesas"}</b> sem nenhuma venda no período.</> },
  ];
}

const COLUNAS = (mediaCasa) => [
  { key: "nome", rotulo: "Mesa", render: (m) => (
    <span className="flex items-center gap-1.5">
      <span className={`font-semibold ${m.excluida ? "text-muted-foreground" : ""}`}>{m.nome}</span>
      <SeloRendimento nivel={m.vendas >= 3 ? rendimento(m.faturamento_hora, mediaCasa) : null} />
    </span>
  ) },
  { key: "faturamento", rotulo: "Faturou", formatar: formatCurrency, barra: true },
  { key: "vendas", rotulo: "Ocupações", formatar: formatInt, dica: "Quantas vezes a mesa foi aberta e fechada" },
  { key: "ticket_medio", rotulo: "Ticket", formatar: (v, m) => (m.vendas ? formatCurrency(v) : "—") },
  { key: "tempo_medio", rotulo: "Tempo", formatar: formatarMinutos, dica: "Tempo médio entre abrir e fechar a mesa" },
  { key: "pessoas_media", rotulo: "Pessoas", formatar: formatarDecimal, dica: "Pessoas por ocupação (quando anotado)" },
  { key: "ticket_pessoa", rotulo: "Por pessoa", formatar: (v) => (v == null ? "—" : formatCurrency(v)) },
  { key: "itens_media", rotulo: "Itens", formatar: formatarDecimal, dica: "Produtos por ocupação" },
  { key: "faturamento_hora", rotulo: "R$ por hora", formatar: porHora, dica: "Faturamento ÷ horas que a mesa ficou ocupada" },
];

const EXCEL = [
  { header: "Mesa", value: (m) => m.nome },
  { header: "Faturamento (R$)", value: (m) => m.faturamento },
  { header: "Ocupações", value: (m) => m.vendas },
  { header: "Ticket médio (R$)", value: (m) => m.ticket_medio },
  { header: "Tempo médio (min)", value: (m) => m.tempo_medio ?? "" },
  { header: "Pessoas por ocupação", value: (m) => m.pessoas_media ?? "" },
  { header: "Ticket por pessoa (R$)", value: (m) => m.ticket_pessoa ?? "" },
  { header: "Itens por ocupação", value: (m) => m.itens_media ?? "" },
  { header: "Horas ocupada", value: (m) => m.horas_ocupada ?? "" },
  { header: "R$ por hora", value: (m) => m.faturamento_hora ?? "" },
];

// Relatório de Tempo → Mesas: mesa a mesa, quanto fatura, quanto tempo fica, quanto rende por hora.
export default function MesasAba({ dados, periodo }) {
  const k = dados.kpis;
  if (!k.vendas) return <Nota>Nenhuma mesa foi aberta nesse período.</Nota>;
  const semPessoas = !k.vendas_com_pessoas;
  const cmp = comparador(k, dados.anterior?.kpis);
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-e2e="kpis-mesas">
        <Kpi destaque rotulo="Tempo médio de mesa" icone={Clock} valor={formatarMinutos(k.tempo_medio)}
          {...cmp("tempo_medio", formatarMinutos, { tipo: "min", melhor: null })}
          detalhe={k.permanencia_mediana != null ? `Metade fica até ${formatarMinutos(k.permanencia_mediana)}` : null} />
        <Kpi rotulo="Faturamento" icone={Coins} valor={formatCurrency(k.faturamento)} detalhe={`${formatInt(k.vendas)} ocupações`}
          {...cmp("faturamento", formatCurrency)} />
        <Kpi rotulo="Ticket médio da mesa" icone={Receipt} valor={formatCurrency(k.ticket_medio)} {...cmp("ticket_medio", formatCurrency)} />
        <Kpi rotulo="R$ por hora de mesa" icone={Gauge} valor={porHora(k.faturamento_hora)} {...cmp("faturamento_hora", porHora)}
          detalhe="Quanto uma mesa ocupada fatura por hora" />
        <Kpi rotulo="Pessoas por mesa" icone={Users} valor={formatarDecimal(k.pessoas_media)} {...cmp("pessoas_media", formatarDecimal)}
          detalhe={semPessoas ? "Anote as pessoas ao abrir a mesa" : `Anotado em ${formatInt(k.vendas_com_pessoas)} de ${formatInt(k.vendas)}`} />
        <Kpi rotulo="Ticket por pessoa" icone={Users} valor={k.ticket_pessoa == null ? "—" : formatCurrency(k.ticket_pessoa)}
          {...cmp("ticket_pessoa", formatCurrency)} />
        <Kpi rotulo="Itens por mesa" icone={Package} valor={formatarDecimal(k.itens_media)} detalhe="Produtos pedidos por ocupação"
          {...cmp("itens_media", formatarDecimal)} />
        <Kpi rotulo="Giro" icone={UtensilsCrossed} valor={k.giro == null ? "—" : `${formatarDecimal(k.giro)}×`}
          {...cmp("giro", (v) => `${formatarDecimal(v)}×`)}
          detalhe={`Vezes que cada mesa é ocupada por dia (${k.mesas_cadastradas} mesas)`} />
      </div>

      <Destaques itens={destaques(dados)} />

      <Secao titulo="Mesa a mesa" icone={UtensilsCrossed} e2e="ranking-mesas"
        explicacao="Toque no título da coluna para ordenar. “Rende bem/pouco” compara o R$ por hora da mesa com a média da casa."
        direita={(
          <Button variant="outline" size="sm" className="gap-1.5 h-8"
            onClick={() => exportarExcel(dados.ranking, EXCEL, `Tempo_Mesas_${rotuloArquivo(periodo)}.xlsx`)}>
            <Download className="w-4 h-4" /> Excel
          </Button>
        )}>
        <Tabela linhas={dados.ranking} colunas={COLUNAS(k.faturamento_hora)} limite={12}
          ordemInicial={{ key: "faturamento", dir: "desc" }} />
      </Secao>

      <MapaMesas dados={dados} />

      <GraficoMetrica titulo="Hora a hora" icone={Clock} e2e="horas-mesas" dados={dados.por_hora}
        explicacao="Em que horário a casa enche, fatura e segura o cliente."
        metricas={[
          { key: "ocupacao_media", rotulo: "Mesas ocupadas", formatar: formatarDecimal, decimais: true,
            explicacao: `Quantas mesas, em média, estavam ocupadas em cada hora (de ${k.mesas_cadastradas}).` },
          { key: "faturamento", rotulo: "Faturamento", formatar: formatCurrency, eixo: reaisEixo,
            explicacao: "Quanto entrou em cada hora, somando o período." },
          { key: "abertas", rotulo: "Mesas abertas", formatar: formatInt, explicacao: "Quantas mesas foram abertas em cada hora." },
          { key: "tempo_medio", rotulo: "Tempo de mesa", formatar: formatarMinutos, eixo: minutosEixo,
            explicacao: "Quanto fica, em média, a mesa aberta naquela hora." },
        ]} />

      <Faixas faixas={dados.faixas} comPessoas titulo="Quanto tempo a mesa fica"
        explicacao="Quantas ocupações caem em cada faixa de tempo e quanto cada faixa gasta." />

      <GraficoMetrica titulo="Dia da semana" icone={CalendarDays} e2e="dias-mesas" dados={dados.por_dia_semana}
        metricas={[
          { key: "faturamento", rotulo: "Faturamento", formatar: formatCurrency, eixo: reaisEixo },
          { key: "vendas", rotulo: "Ocupações", formatar: formatInt },
          { key: "ticket_medio", rotulo: "Ticket", formatar: formatCurrency, eixo: reaisEixo },
          { key: "tempo_medio", rotulo: "Tempo de mesa", formatar: formatarMinutos, eixo: minutosEixo },
        ]} />

      <div className="space-y-1">
        {k.pelo_qr > 0 && <Nota>Mesa QR entra junto: {formatInt(k.pelo_qr)} das {formatInt(k.vendas)} ocupações tiveram pedido pelo QR. Garçom e QR na mesma mesa contam como uma ocupação.</Nota>}
        {k.abertas_agora > 0 && <Nota>{formatInt(k.abertas_agora)} {k.abertas_agora === 1 ? "mesa ainda aberta entra" : "mesas ainda abertas entram"} no faturamento, mas não no tempo.</Nota>}
        {k.sem_tempo > 0 && <Nota>{formatInt(k.sem_tempo)} {k.sem_tempo === 1 ? "mesa ficou" : "mesas ficaram"} fora do tempo médio (mais de 12h aberta — esquecida).</Nota>}
        {semPessoas && <Nota>Pessoas por mesa começa a contar a partir de agora: o número que o garçom anota ao abrir a mesa fica guardado quando ela fecha.</Nota>}
      </div>
    </>
  );
}
